/**
 * Analytics for the Better Odds feature.
 *
 * Prompt-shown and listing-clicked only fire from the My Bids strip — the
 * homepage carousel relies on BaseCarousel's existing generic impression/
 * click telemetry (tagged with `carouselName: 'better_odds'`) instead of a
 * second, parallel event set. Bid-placed fires from wherever the buyer
 * actually places the bid (the listing page), attributed back here via the
 * click-through marker regardless of which surface sent them there.
 */

type Track = (eventName: string, properties: Record<string, unknown>) => void

export const trackBetterOddsPromptShown = (
  track: Track,
  args: {
    surface: 'my_bids' | 'homepage'
    lostListingId?: string
    category?: string
    candidateCount: number
  }
) => {
  track('better_odds_prompt_shown', {
    surface: args.surface,
    lost_listing_id: args.lostListingId,
    category: args.category,
    candidate_count: args.candidateCount,
  })
}

export const trackBetterOddsListingClicked = (
  track: Track,
  args: {
    surface: 'my_bids' | 'homepage'
    targetListingId: string
    targetUniqueBidders?: number
    targetGap?: number
    position: number
  }
) => {
  track('better_odds_listing_clicked', {
    surface: args.surface,
    target_listing_id: args.targetListingId,
    target_unique_bidders: args.targetUniqueBidders,
    target_gap: args.targetGap,
    position: args.position,
  })
}

export const trackBetterOddsBidPlaced = (
  track: Track,
  args: {
    targetListingId: string
    wasFirstBidOnListing: boolean
  }
) => {
  track('better_odds_bid_placed', {
    target_listing_id: args.targetListingId,
    was_first_bid_on_listing: args.wasFirstBidOnListing,
  })
}

const CLICK_THROUGH_STORAGE_KEY = 'better_odds_click_through'

type ClickThrough = { listingId: string; at: number }

/**
 * Records the target listing of a Better Odds click-through so
 * `usePlaceBidMutation` can attribute a later bid back to this feature.
 */
export const markBetterOddsClickThrough = (listingId: string) => {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(
      CLICK_THROUGH_STORAGE_KEY,
      JSON.stringify({ listingId, at: Date.now() } satisfies ClickThrough)
    )
  } catch {
    // sessionStorage can throw (private browsing, quota) — attribution is
    // best-effort, never worth failing the click-through over.
  }
}

/** The click-through marker, if one is stored and parses cleanly. */
export const getBetterOddsClickThrough = (): ClickThrough | null => {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(CLICK_THROUGH_STORAGE_KEY)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof (parsed as ClickThrough).listingId === 'string'
    ) {
      return parsed as ClickThrough
    }
    return null
  } catch {
    return null
  }
}

/**
 * Clears the marker after it's been used for attribution (or found stale),
 * so an unrelated later bid never gets mis-attributed to this feature.
 */
export const clearBetterOddsClickThrough = () => {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.removeItem(CLICK_THROUGH_STORAGE_KEY)
  } catch {
    // best-effort, same as above
  }
}
